import { useState, useEffect, useCallback } from 'react';
import type { UserPreferences } from '@/lib/types';
import { getPreferences, updatePreferences } from '@/lib/storage';
import { DEFAULT_PREFERENCES } from '@/lib/constants';

// Module-level shared state — all hook instances stay in sync
let cachedPrefs: UserPreferences | null = null;
let cacheLoading = true;
const listeners = new Set<(prefs: UserPreferences) => void>();

function notifyAll(prefs: UserPreferences) {
  cachedPrefs = prefs;
  listeners.forEach((fn) => fn(prefs));
}

interface UsePreferencesResult {
  prefs: UserPreferences;
  updatePreference: (updates: Partial<UserPreferences>) => Promise<void>;
  isLoading: boolean;
}

export function usePreferences(): UsePreferencesResult {
  const [prefs, setPrefs] = useState<UserPreferences>(
    cachedPrefs ?? { ...DEFAULT_PREFERENCES, categoryFilter: [...DEFAULT_PREFERENCES.categoryFilter] }
  );
  const [isLoading, setIsLoading] = useState(cacheLoading);

  useEffect(() => {
    listeners.add(setPrefs);
    if (cacheLoading) {
      getPreferences().then((stored) => {
        cacheLoading = false;
        notifyAll(stored);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
    return () => { listeners.delete(setPrefs); };
  }, []);

  const updatePreference = useCallback(async (updates: Partial<UserPreferences>) => {
    const updated = await updatePreferences(updates);
    notifyAll(updated);
  }, []);

  return { prefs, updatePreference, isLoading };
}
